import React, { useState, useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useUser } from '../../../../store/useUser';
import { Lock } from 'lucide-react';

const LOGIN_PASSWORD = gql`
    mutation ValidatePassword($username: String!, $password: String!) {
        validatePassword(username: $username, password: $password)
    }
`;
export const LockScreen = ({onSuccess} : { onSuccess: () => void }) => {
    const { user } = useUser();

    const [state, setState] = useState({
        username: '',
        password: '',
        error: '',        
        loading: false,
        
    })

    const [loginPassword] = useMutation(LOGIN_PASSWORD);

    const username = typeof user?.username === 'string' ? user.username : '';

    useEffect(() => {
      if (!username) {
        return;
      }

      setState((prev) => {
        if (prev.username === username) {
          return prev;
        }
        return { ...prev, username };
      });
    }, [username]);

    const handleUsername = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setState((prev) => ({ ...prev, loading: true, error: '' }));
        if (!state.username || !state.password) {
            setState((prev) => ({ ...prev, loading: false, error: 'Password is required' }));
            return;
        }
        try {
            const loginPasswordResult = await loginPassword({ variables: { username: state.username, password: state.password } });
            if (loginPasswordResult?.data?.validatePassword) {
                setState((prev) => ({ ...prev, loading: false, password: '', error: '' }));
                onSuccess();
                return;
            }
            setState((prev) => ({ ...prev, loading: false, error: 'Invalid credentials' }));
        } catch (error) {
            setState((prev) => ({ ...prev, loading: false, error: 'Invalid credentials' }));
        }
    }

    return (
        <div className="flex h-full min-h-full items-center justify-center px-4 py-6">
          <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-lg">
            <img 
              src={`${process.env.PUBLIC_URL}/logoODR.png`} 
              alt="logo" 
              className="w-48 mx-auto"
            />
            
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-2">
                <Lock className="w-5 h-5 text-gray-600" />
                <h1 className="text-2xl font-bold text-gray-900">Unlock Session</h1>
              </div>
              
              <div className="space-y-2 text-gray-600">
                <p>For security reasons, your session has been locked.</p>
                <p>Please enter your password to unlock.</p>
              </div>
              
              <form onSubmit={handleUsername} className="mt-8 space-y-6">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="lock-screen-username" className="block text-sm font-medium text-gray-700 text-left">
                      Username
                    </label>
                    <input
                      id="lock-screen-username"
                      type="text"
                      value={state.username}
                      readOnly
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700 sm:text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="lock-screen-password" className="block text-sm font-medium text-gray-700 text-left">
                      Password
                    </label>
                    <input
                      id="lock-screen-password"
                      type="password"
                      value={state.password}
                      onChange={(e) => setState((prev) => ({ ...prev, password: e.target.value, error: '' }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                {state.error && (
                  <p className="text-sm text-red-600" role="alert">
                    {state.error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={state.loading}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  {state.loading ? 'Unlocking...' : 'Unlock'}
                </button>
              </form>
            </div>
          </div>
        </div>
      );
} 
